/**
 * MUIDataTable serverSide: filtre + pagination tüm sorguya gider.
 *
 * Kullanım (class component):
 *   state: { datas: [], count: 0, page: 0, rowsPerPage: 50, search: '', filters: {} }
 *   getDatas() → fetchServerPage()
 *   options: withServerSideOptions(options, this)
 */

/**
 * Axios response veya ham body kabul eder.
 * Body: array (tercih) veya {items|data|liste, count}.
 * Toplam: X-Total-Count header veya body.count.
 */
export function parseListResponse(resOrData) {
  const isAxios = resOrData && typeof resOrData === "object" && "data" in resOrData && "headers" in resOrData;
  const raw = isAxios ? resOrData.data : resOrData;
  const headers = (isAxios && resOrData.headers) || {};
  const headerCount = headers["x-total-count"] ?? headers["X-Total-Count"];

  if (Array.isArray(raw)) {
    return {
      items: raw,
      count: headerCount != null ? Number(headerCount) : raw.length,
    };
  }
  if (!raw || typeof raw !== "object") {
    return { items: [], count: 0 };
  }
  const items = raw.items || raw.data || raw.liste || [];
  const count =
    headerCount != null
      ? Number(headerCount)
      : raw.count != null
        ? Number(raw.count)
        : raw.total != null
          ? Number(raw.total)
          : Array.isArray(items)
            ? items.length
            : 0;
  return { items: Array.isArray(items) ? items : [], count };
}

/** FE kolon adı → API query key */
const FILTER_KEY_MAP = {
  teknik_personeller: "personel",
  talep_edenler: "personel",
  seri_nolar: "seri",
  seri_no: "seri",
  uyfler: "uyf",
  uyf_no: "uyf",
  durum: "filter_durum",
  tip: "tip",
  musteri: "musteri",
  temsilci: "temsilci",
  teklif_tipi: "teklif_tipi",
  msn: "msn",
};

/** Tip etiket → API flag */
const TIP_FLAG = {
  Garanti: "garanti",
  Kurulum: "kurulum",
  Servis: "servis",
};

/** MUI filterList + columns → flat query params (çoklu değer dizi). */
export function filtersFromTableState(tableState) {
  const out = {};
  if (!tableState) return out;
  const cols = tableState.columns || [];
  const lists = tableState.filterList || [];
  cols.forEach((col, idx) => {
    const name = col && col.name;
    if (!name) return;
    const vals = lists[idx];
    if (!vals || !vals.length) return;
    out[name] = vals.length === 1 ? vals[0] : vals;
  });
  return out;
}

export function buildPageParams({
  page = 0,
  rowsPerPage = 50,
  search = "",
  filters = {},
  sortBy = "",
  sortDir = "",
  extra = {},
} = {}) {
  const params = {
    page: Number(page) + 1,
    page_size: Number(rowsPerPage) || 50,
    ...extra,
  };
  const q = (search || "").trim();
  if (q) params.q = q;
  const dir = String(sortDir || "").toLowerCase();
  if (sortBy && (dir === "asc" || dir === "desc")) {
    params.sort_by = sortBy;
    params.sort_dir = dir;
  }

  Object.entries(filters || {}).forEach(([key, val]) => {
    if (val == null || val === "") return;
    const apiKey = FILTER_KEY_MAP[key] || key;
    if (apiKey === "tip") {
      const labels = Array.isArray(val) ? val : [val];
      const flags = labels.map((l) => TIP_FLAG[l]).filter(Boolean);
      // İş emri: Garanti/Kurulum/Servis → flag dizisi; teklif: etiket string
      if (flags.length && flags.length === labels.length) {
        params.tip = flags;
      } else if (labels.length) {
        params.tip = labels.join(",");
      }
      return;
    }
    if (Array.isArray(val)) {
      // çoklu seçim → virgülle (ILIKE OR) veya tekrarlayan param
      params[apiKey] = val.join(",");
    } else {
      params[apiKey] = val;
    }
  });
  return params;
}

const RELOAD_ACTIONS = new Set([
  "changePage",
  "changeRowsPerPage",
  "search",
  "filterChange",
  "sort",
  "resetFilters",
]);

/**
 * @param {object} options mevcut MUI options
 * @param {object} ctx { count, page, rowsPerPage, onServerTableChange }
 */
export function withServerSideOptions(options = {}, ctx = {}) {
  const {
    count = 0,
    page = 0,
    rowsPerPage = 50,
    onServerTableChange,
    sortBy,
    sortDir,
  } = ctx;
  const prev = options.onTableChange;
  const dir = String(sortDir || "").toLowerCase();
  const sortOrder =
    sortBy && (dir === "asc" || dir === "desc")
      ? { name: sortBy, direction: dir }
      : options.sortOrder;

  return {
    ...options,
    sortOrder,
    serverSide: true,
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions: options.rowsPerPageOptions || [25, 50, 100],
    onTableChange: (action, tableState) => {
      if (typeof prev === "function") prev(action, tableState);
      if (!RELOAD_ACTIONS.has(action)) return;
      if (typeof onServerTableChange === "function") {
        onServerTableChange(action, tableState);
      }
    },
  };
}

/** Class component için standart handler üretir. */
export function makeServerTableHandler(component, fetchMethodName = "getDatas") {
  return (action, tableState) => {
    const filters = filtersFromTableState(tableState);
    const sort = tableState.sortOrder || {};
    const next = {
      page: action === "sort" ? 0 : tableState.page,
      rowsPerPage: tableState.rowsPerPage,
      search: tableState.searchText || "",
      filters,
      sortBy: sort.name || "",
      sortDir: sort.direction || "",
    };
    component.setState(next, () => {
      const fn = component[fetchMethodName];
      if (typeof fn === "function") fn.call(component);
    });
  };
}
